  <template>
  <div class="Slider">

    <transition-group name="Slider-arrows-">

      <div class="Slider-arrowPrevious"
        v-if="showPreviousArrow"
        key="sliderArrowPrevious"
        @click="previous">

        <icon name="arrow-left" width="12.41px" height="20.578px" />

      </div>

      <div class="Slider-arrowNext"
        v-if="showNextArrow"
        key="sliderArrowNext"
        @click="next">

        <icon name="arrow-right" width="12.41px" height="20.578px" />

      </div>

    </transition-group>

    <div class="Slider-dots"
      v-if="items.length > 1">

      <div class="Slider-dot"
        v-for="(item, index) in items"
        :class="{ 'Slider-dot--isSelected': index === selectedIndex }">
      </div>

    </div>

    <carousel class="Slider-carousel"
      ref="carousel"
      :initial-index="initialIndex"
      :next="goNext"
      :prev="goPrev"
      :restart="goRestart">

      <div class="Slider-item"
        v-for="(item, index) in items"
        ref="items">

        <div class="Slider-itemContent"
          v-if="item.template && item.template !== 'video'">

          <div class="Slider-template">

            <component
              :is="`slider-${item.template || 'text'}`" 
              :content="item.content" />

          </div>

        </div>

        <slider-video
          v-if="item.template === 'video'"
          :selected="index === selectedIndex"
          :url="item.video" />

      </div>

    </carousel>

    <div class="Slider-images">

      <div class="Slider-imagesGradient"></div>

      <div class="Slider-image"
        v-for="(image, index) in images"
        ref="images"
        :style="imageStyle(image, index)">
      </div>

    </div>

  </div>
</template>

<script>
import Carousel from 'components/Carousel'
import Icon from 'components/Icon'
import SliderShare from 'components/Slider/Share'
import SliderText from 'components/Slider/Text'
import SliderVideo from 'components/Slider/Video'

export default {
  name: 'Slider',

  components: {
    Carousel,
    Icon,
    SliderShare,
    SliderText,
    SliderVideo
  },

  props: {
    items: {
      type: Array,
      required: true
    },
    restart: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      images: [],
      sliderX: 0,
      initialIndex: 0,
      selectedIndex: 0,
      goPrev: false,
      goNext: false,
      goRestart: false,
      showNextArrow: false,
      showPreviousArrow: false
    }
  },

  created () {
    // Set initial index to page query
    this.initialIndex = this.$route.query.page - 1 || 0
  },

  mounted () {
    this.$nextTick(this.init)
  },

  watch: {
    sliderX () {
      this.updateImages()
    },

    selectedIndex () {
      // Push page arg to router
      this.$router.push({
        query: {
          page: parseInt(this.selectedIndex) + 1
        }
      })

      // Show or hide arrows
      this.showHideArrows()
    },

    restart () {
      this.goRestart = this.restart
    }
  },

  methods: {
    init () {
      this.initImages()
      this.showHideArrows()
      this.addEventListeners()
    },

    /**
     * Go to next slide
     */
    next () {
      this.goNext = true
      this.$nextTick(() => { this.goNext = false })
    },

    /**
     * Go to previous slide
     */
    previous () {
      this.goPrev = true
      this.$nextTick(() => { this.goPrev = false })
    },

    /**
     * Add event listeners
     */
    addEventListeners () {
      this.$refs.carousel.$el.addEventListener('slide', this.handleSlide)
      this.$refs.carousel.$el.addEventListener('selected', this.handleSelected)
    },

    /**
     * Remove event listeners
     */
    removeEventListeners () {
      this.$refs.carousel.$el.removeEventListener('slide', this.handleSlide)
      this.$refs.carousel.$el.removeEventListener('selected', this.handleSelected)
    },

    /**
     * Handle carousel slide event
     * @param  {Object} event
     */
    handleSlide (event) {
      this.sliderX = event.detail.x
    },

    /**
     * Handle carousel selected event
     * @param  {Object} event
     */
    handleSelected (event) {
      this.selectedIndex = event.detail.index
    },

    /**
     * Organize background images
     */
    initImages () {
      const images = []
      const startPositions = {}

      this.items.forEach((item, i) => {
        // Get corresponding HTMLElement
        const $item = this.$refs.items[i]

        // Get x position of element
        const xPos = $item.offsetLeft

        // Add a start position for this item's image
        if (startPositions[item.image] === undefined) {
          startPositions[item.image] = xPos
        }

        if (startPositions[item.video] === undefined) {
          startPositions[item.video] = xPos
        }

        // Get the index of the last item with the same image
        const lastDuplicateImageIndex = this.getLastDuplicateImageIndex(i, item.image)

        if (i === lastDuplicateImageIndex) {
          images.push({
            src: item.image,
            range: [startPositions[item.image], this.$refs.items[lastDuplicateImageIndex].offsetLeft]
          })
        }
      })

      this.images = images
    },

    /**
     * Get the index of the last item with the same image as a given item
     * @param {Integer} index
     * @param {String} image
     * @return {Integer}
     */
    getLastDuplicateImageIndex (index, image) {
      const nextIndex = index + 1

      // Look ahead to see if the next item has the same image
      if (this.items[nextIndex] && this.items[nextIndex].image === image) {
        // Check again
        return this.getLastDuplicateImageIndex(nextIndex, image)
      }

      return index
    },

    /**
     * Update background image position when slider moves
     */
    updateImages () {
      if (!this.$refs.images) return
      let x = this.sliderX * -1

      this.images.forEach((image, i) => {
        if (!this.$refs.images[i]) return
        let imageX = image.range[0] - x

        // First image should not move beyond begining boundary
        if ((i === 0) && x < 0) {
          return
        }

        // Last image should not move beyond end boundary
        if ((i === this.images.length - 1) && x > image.range[1]) {
          return
        }

        // Images without ranges move normally
        if (image.range[0] === image.range[1]) {
          this.$refs.images[i].style.transform = `translate3d(${imageX}px, 0, 0)`
          return
        }

        // If x is within image range, hold position at 0
        if (x > image.range[0] && x <= image.range[1]) {
          imageX = 0
        }

        // If we're over image range
        if (x > image.range[1]) {
          imageX = image.range[1] - x
        }

        this.$refs.images[i].style.transform = `translate3d(${imageX}px, 0, 0)`
      })
    },

    /**
     * Show or hide previous/next arrows
     */
    showHideArrows () {
      // Hide arrows for less than 2 items
      if (this.items.length < 2) {
        this.showPreviousArrow = false
        this.showNextArrow = false
        return
      }

      // Show previous arrow?
      this.showPreviousArrow = this.selectedIndex > 0

      // Show next arrow?
      this.showNextArrow = this.selectedIndex < this.items.length - 1
    },

    /**
     * Set initial background image style
     * @param  {Object} image
     * @param  {Number} index
     * @return {Object}
     */
    imageStyle (image, index) {
      return {
        'transform': `translate3d(${image.range[0]}px, 0, 0)`,
        'z-index': 100 - index,
        'background-image': `url(${encodeURI(image.src)})`
      }
    }
  }
}
</script>

<style>
.Slider {
  z-index: 0;
  flex-grow: 1;
}

.Slider-carousel {
  width: 930px;
  height: 930px;
}

.Slider-slider {
  position: relative;
}

.Slider-item {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 930px;
  height: 930px;
}

.Slider-itemContent {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 719px;
  height: 719px;
  background-color: rgba(255,255,255,0.8);
  border-radius: 50%;
}

.Slider-item:first-child .Slider-itemContent {
  transform: translateX(150%);
  animation: introSlide 0.5s 0.5s ease forwards;
}

@keyframes introSlide {
  0% { transform: translateX(150%); }
  100% { transform: translateX(0%); }
}

.Slider-template {
  max-width: 640px;
}

.Slider-images {
  position: absolute;
  z-index: -1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.Slider-imagesGradient {
  position: absolute;
  z-index: 999;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(to bottom, #fff 0%, 74.38%, #808081 100%);
  mix-blend-mode: multiply;
}

.Slider-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 930px;
  height: 930px;
  background-repeat: none;
  background-position: center;
  background-size: cover;
}

.Slider-dots {
  position: absolute;
  z-index: 10000;
  right: 0;
  bottom: 48px;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.Slider-dot {
  margin: 0 1.5px;
  width: 16px;
  height: 16px;
  border: 2px solid var(--color-1);
  border-radius: 50%;
  background-color: rgba(255,255,255,0.5);
  opacity: 1;
}

.Slider-dot--isSelected {
  background-color: var(--color-3);
}

.Slider-arrows--enter-active,
.Slider-arrows--leave-active {
  transition: opacity 0.25s ease;
}

.Slider-arrows--enter,
.Slider-arrows--leave-active {
  opacity: 0;
}

.Slider-arrowPrevious,
.Slider-arrowNext {
  position: absolute;
  z-index: 100;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -65px;
  width: 130px;
  height: 130px;
  background-color: rgba(255,255,255,0.8);
  border-radius: 50%;
}

.Slider-arrowPrevious svg,
.Slider-arrowNext svg {
  fill: var(--color-3);
}

.Slider-arrowPrevious {
  left: -65px;
}

.Slider-arrowNext {
  right: -65px;
}

.Slider-arrowPrevious > * {
  position: relative;
  left: 22px;
}

.Slider-arrowNext > * {
  position: relative;
  right: 22px;
}
</style>



// WEBPACK FOOTER //
// Slider.vue?2cf437c9